import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FolderPlus, FileText, Trash2, ChevronRight, ChevronDown, Plus, Search, X, Menu, CloudOff, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import EmptyState from '../components/EmptyState.jsx';
import NoteEditor from '../components/notes/NoteEditor.jsx';
import { api } from '../services/api.js';
import { syncEngine } from '../services/sync.js';
import { useStorage, useSyncQueue } from '../hooks/useStorage.js';
import { useFocusRefresh } from '../hooks/useFocusRefresh.js';
import { useNotificationRefresh } from '../hooks/useNotificationRefresh.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { cn } from '../lib/utils.js';
import { localDB } from '../services/db.js';

const NoteTree = ({ notes, parentId, expandedFolders, onToggle, depth, onNoteSelect, selectedNoteId, initialNotes = null, onDropNote, dragOverNote, visitedNoteIds = new Set() }) => {
  const childNotes = useMemo(() => 
    initialNotes || notes.filter((n) => n.parent_id === parentId),
    [initialNotes, notes, parentId]
  );
  
  if (childNotes.length === 0) return null;

  return (
    <div style={{ paddingLeft: depth === 0 ? 0 : 16 }}>
      {childNotes.map((note) => {
        const hasChildren = notes.some((n) => n.parent_id === note.id);
        const isExpanded = expandedFolders.has(`note-${note.id}`);
        const isDragOver = dragOverNote === note.id;
        
        return (
          <div key={note.id}>
            <div
              className={cn(
                'group flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm transition cursor-pointer',
                selectedNoteId === note.id ? 'bg-accent/15 text-accent' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-300',
                isDragOver && 'bg-accent/20 ring-1 ring-accent',
                note.pending && 'opacity-70'
              )}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', note.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                onDropNote?.(e, note.id, 'over');
              }}
              onDragLeave={() => onDropNote?.(null, null, 'leave')}
              onDrop={(e) => onDropNote?.(e, note.id, 'drop')}
              onClick={() => onNoteSelect(note)}
            >
              {hasChildren ? (
                <span
                  onClick={(e) => { e.stopPropagation(); onToggle(`note-${note.id}`); }}
                  className="cursor-pointer p-0.5 hover:bg-white/10 rounded"
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              ) : (
                <span className="w-[14px]" />
              )}
              <span>{note.icon}</span>
              <span className="truncate flex-1">{note.title || 'Untitled'}</span>
              {note.pending && <CloudOff size={10} className="text-amber-500" />}
            </div>
            {isExpanded && hasChildren && !visitedNoteIds.has(note.id) && (
              <NoteTree
                notes={notes}
                parentId={note.id}
                expandedFolders={expandedFolders}
                onToggle={onToggle}
                depth={depth + 1}
                onNoteSelect={onNoteSelect}
                selectedNoteId={selectedNoteId}
                onDropNote={onDropNote}
                dragOverNote={dragOverNote}
                visitedNoteIds={new Set([...visitedNoteIds, note.id])}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const FolderTree = ({ folders, notes, selectedFolderId, onSelect, expandedFolders, onToggle, depth = 0, onDeleteFolder, onNoteSelect, selectedNoteId, onDropNote, dragOverFolder, parentId = null, visitedFolderIds = new Set() }) => {
  const currentFolders = useMemo(() => 
    folders.filter((f) => {
      if (parentId === null) {
        return !f.parent_id;
      }
      return f.parent_id === parentId;
    }),
    [folders, parentId]
  );
  
  return currentFolders.map((folder) => {
    const isExpanded = expandedFolders.has(folder.id);
    const children = folders.filter((f) => f.parent_id === folder.id);
    const hasChildren = children.length > 0;
    const folderNotes = notes.filter((n) => n.folder_id === folder.id && !n.parent_id);
    const hasNotes = folderNotes.length > 0;
    const isDragOver = dragOverFolder === folder.id;
    
    return (
      <div key={folder.id}>
        <div
          className={cn(
            'group flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm transition cursor-pointer',
            selectedFolderId === folder.id ? 'bg-accent/15 text-accent' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-300',
            isDragOver && 'bg-accent/20 ring-1 ring-accent'
          )}
          onClick={() => onSelect(folder.id)}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            onDropNote?.(e, folder.id, 'folder-over');
          }}
          onDragLeave={() => onDropNote?.(null, null, 'leave')}
          onDrop={(e) => onDropNote?.(e, folder.id, 'folder-drop')}
        >
          <span
            onClick={(e) => { e.stopPropagation(); onToggle(folder.id); }}
            className="cursor-pointer p-0.5 hover:bg-white/10 rounded"
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <span>{folder.icon}</span>
          <span className="truncate flex-1">{folder.name}</span>
          {!String(folder.id).startsWith('temp-') && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteFolder(folder.id, folder.name); }}
              className="hidden p-1 hover:bg-rose-500/20 hover:text-rose-400 rounded transition group-hover:block"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
        
        {isExpanded && hasNotes && (
          <div className="border-l border-zinc-800 ml-2.5 my-0.5">
            <NoteTree
              notes={notes}
              initialNotes={folderNotes}
              expandedFolders={expandedFolders}
              onToggle={onToggle}
              depth={0}
              onNoteSelect={onNoteSelect}
              selectedNoteId={selectedNoteId}
              onDropNote={onDropNote}
              dragOverNote={dragOverFolder}
            />
          </div>
        )}
        
        {isExpanded && hasChildren && !visitedFolderIds.has(folder.id) && (
          <div className="border-l border-zinc-800 ml-2.5 my-0.5">
            <FolderTree
              folders={folders}
              notes={notes}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
              expandedFolders={expandedFolders}
              onToggle={onToggle}
              depth={depth + 1}
              onDeleteFolder={onDeleteFolder}
              onNoteSelect={onNoteSelect}
              selectedNoteId={selectedNoteId}
              onDropNote={onDropNote}
              dragOverFolder={dragOverFolder}
              parentId={folder.id}
              visitedFolderIds={new Set([...visitedFolderIds, folder.id])}
            />
          </div>
        )}
      </div>
    );
  });
};

const NotesSearch = ({ notes, folders, onNoteSelect, onClose }) => {
  const [query, setQuery] = useState('');
  
  const results = query.trim()
    ? notes.filter((n) =>
        n.title?.toLowerCase().includes(query.toLowerCase()) ||
        n.content?.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  return (
    <div className="p-3 border-b border-border/40">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search notes..."
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-8 pr-8 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none focus:ring-1 focus:ring-accent"
          autoFocus
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {results.length > 0 && (
        <div className="mt-2 max-h-48 overflow-y-auto">
          {results.map((note) => (
            <div
              key={note.id}
              className="flex items-center gap-2 px-2 py-2 rounded hover:bg-white/5 cursor-pointer text-sm"
              onClick={() => {
                onNoteSelect(note);
                onClose();
              }}
            >
              <span>{note.icon}</span>
              <span className="truncate text-zinc-300">{note.title || 'Untitled'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Notes() {
  const toast = useToast();
  const toastRef = useRef(toast);
  const [apiLoading, setApiLoading] = useState(false);
  const apiLoadingRef = useRef(false);
  const [apiError, setApiError] = useState(null);
  const retryCount = useRef(0);
  const retryTimeoutRef = useRef(null);
  const MAX_RETRIES = 3;

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [dragOverNote, setDragOverNote] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const { data: folders, loading: foldersLoading, refresh: refreshFolders, saveItem: saveLocalFolder, removeItem: removeLocalFolder } = useStorage('folders');
  const { data: notes, loading: notesLoading, refresh: refreshNotes, saveItem: saveLocalNote, removeItem: removeLocalNote } = useStorage('notes');
  const { queue, addToQueue } = useSyncQueue();

  // Audit notes distribution
  useEffect(() => {
    if (!notesLoading && notes.length > 0) {
      const unfiled = notes.filter(n => !n.folder_id && !n.parent_id);
      const withFolder = notes.filter(n => n.folder_id);
      const childNotes = notes.filter(n => n.parent_id);
      
      console.log('[Notes Audit]', {
        total: notes.length,
        unfiled: unfiled.length,
        withFolder: withFolder.length,
        childNotes: childNotes.length,
        check: unfiled.length + withFolder.length + childNotes.length === notes.length ? 'OK' : 'MISMATCH'
      });
    }
  }, [notes, notesLoading]);

  const loadData = useCallback(async () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (apiLoadingRef.current || retryCount.current >= MAX_RETRIES) return;

    try {
      apiLoadingRef.current = true;
      setApiLoading(true);
      setApiError(null);
      console.log(`[Notes] Fetching data (Attempt ${retryCount.current + 1})...`);

      const [folderRes, noteRes] = await Promise.all([
        api.listFolders(),
        api.listNotes(''),
      ]);

      if (folderRes?.data?.items) {
        const apiFolders = folderRes.data.items;
        const apiFolderIds = new Set(apiFolders.map(f => f.id));
        
        // Prune deleted folders from local DB
        const currentLocalFolders = await localDB.getAll('folders');
        for (const folder of currentLocalFolders) {
          if (!folder.pending && !apiFolderIds.has(folder.id)) {
            await localDB.delete('folders', folder.id);
          }
        }

        // Update with fresh server data
        for (const f of apiFolders) {
          await localDB.put('folders', f);
        }
      }

      if (noteRes?.data?.items) {
        const apiNotes = noteRes.data.items;
        const apiNoteIds = new Set(apiNotes.map(n => n.id));

        // Prune deleted notes from local DB
        const currentLocalNotes = await localDB.getAll('notes');
        for (const note of currentLocalNotes) {
          if (!note.pending && !apiNoteIds.has(note.id)) {
            await localDB.delete('notes', note.id);
          }
        }

        // Update with fresh server data
        for (const n of apiNotes) {
          await localDB.put('notes', n);
        }
      }

      retryCount.current = 0; // Reset on success
      await refreshFolders();
      await refreshNotes();
    } catch (err) {
      console.warn(`[Notes] API Load failed (Attempt ${retryCount.current + 1}):`, err);

      if (retryCount.current < MAX_RETRIES - 1) {
        const delay = Math.pow(2, retryCount.current + 1) * 1000;
        console.log(`[Notes] Retrying in ${delay}ms...`);
        retryTimeoutRef.current = setTimeout(() => {
          retryCount.current++;
          loadData();
        }, delay);
      } else {
        retryCount.current = MAX_RETRIES;
        setApiError('Failed to load data from server. Working with local cache.');
        toastRef.current.error('Could not sync notes with server.', 'Sync Error');
      }
    } finally {
      apiLoadingRef.current = false;
      setApiLoading(false);
    }
  }, [refreshFolders, refreshNotes]);

  useEffect(() => {
    loadData();
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [loadData]);

  useFocusRefresh(loadData);
  useNotificationRefresh(loadData, 'syncvault:refresh:notes');

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    const tempId = `temp-f-${Date.now()}`;
    const payload = {
      id: tempId,
      name: newFolderName.trim(),
      parent_id: selectedFolderId === 'unfiled' ? null : (selectedFolderId || null),
      icon: '📁',
      sort_order: 0,
      created_at: Date.now(),
      updated_at: Date.now(),
      pending: true
    };
    
    try {
      await saveLocalFolder(payload);
      setNewFolderName('');
      setShowNewFolder(false);
      
      const res = await api.createFolder({ name: payload.name, parent_id: payload.parent_id });
      await removeLocalFolder(tempId);
      await saveLocalFolder(res.data);
      toast.success('Folder created');
    } catch (err) {
      console.warn('[Notes] Folder sync failed:', err);
      await addToQueue({ type: 'CREATE_FOLDER', data: { name: payload.name, parent_id: payload.parent_id } });
      toast.info('Created locally (offline)');
      syncEngine.sync();
    }
  };

  const deleteFolder = async (id, name) => {
    if (!confirm(`Delete folder "${name}"?\n\nNotes inside will be moved to Unfiled.`)) return;
    try {
      await removeLocalFolder(id);
      if (selectedFolderId === id) setSelectedFolderId(null);
      
      if (!String(id).startsWith('temp-')) {
        await api.deleteFolder(id);
      }
      toast.info('Folder deleted');
    } catch (err) {
      console.warn('[Notes] Folder delete failed:', err);
      await addToQueue({ type: 'DELETE_FOLDER', id });
      toast.info('Deleted locally (offline)');
      syncEngine.sync();
    }
  };

  const createNote = async () => {
    const tempId = `temp-n-${Date.now()}`;
    const folderId = selectedFolderId === 'unfiled' ? null : (selectedFolderId || null);
    const payload = {
      id: tempId,
      title: 'Untitled',
      content: '',
      folder_id: folderId,
      parent_id: null,
      icon: '📄',
      sort_order: 0,
      pinned: 0,
      created_at: Date.now(),
      updated_at: Date.now(),
      pending: true
    };

    try {
      await saveLocalNote(payload);
      setSelectedNote(payload);
      setMobileSidebarOpen(false);
      
      const res = await api.createNote({ title: payload.title, content: payload.content, folder_id: payload.folder_id });
      await removeLocalNote(tempId);
      await saveLocalNote(res.data);
      setSelectedNote(res.data);
      toast.success('Note created');
    } catch (err) {
      console.warn('[Notes] Note sync failed:', err);
      await addToQueue({ type: 'CREATE_NOTE', data: { title: payload.title, content: payload.content, folder_id: payload.folder_id } });
      toast.info('Created locally (offline)');
      syncEngine.sync();
    }
  };

  const createChildPage = async (parentId) => {
    const tempId = `temp-n-${Date.now()}`;
    const payload = {
      id: tempId,
      title: 'Untitled',
      content: '',
      folder_id: null,
      parent_id: parentId,
      icon: '📄',
      sort_order: 0,
      pinned: 0,
      created_at: Date.now(),
      updated_at: Date.now(),
      pending: true
    };

    try {
      await saveLocalNote(payload);
      setSelectedNote(payload);
      
      const res = await api.createNote({
        title: payload.title,
        content: payload.content,
        folder_id: null,
        parent_id: parentId,
      });
      await removeLocalNote(tempId);
      await saveLocalNote(res.data);
      setSelectedNote(res.data);
      toast.success('Page created');
    } catch (err) {
      console.warn('[Notes] Child page sync failed:', err);
      await addToQueue({ type: 'CREATE_NOTE', data: { title: payload.title, content: payload.content, parent_id: parentId } });
      toast.info('Created locally (offline)');
      syncEngine.sync();
    }
  };

  const saveNote = async (noteId, updates) => {
    const originalNote = notes.find(n => n.id === noteId);
    if (!originalNote) return;
    
    const updatedNote = { ...originalNote, ...updates, updated_at: Date.now(), pending: true };
    
    try {
      await saveLocalNote(updatedNote);
      if (selectedNote?.id === noteId) {
        setSelectedNote(updatedNote);
      }
      
      if (!String(noteId).startsWith('temp-')) {
        const res = await api.updateNote(noteId, updates);
        await saveLocalNote(res.data);
        if (selectedNote?.id === noteId) setSelectedNote(res.data);
      }
    } catch (err) {
      console.warn('[Notes] Note save failed:', err);
      await addToQueue({ type: 'UPDATE_NOTE', id: noteId, data: updates });
      syncEngine.sync();
    }
  };

  const deleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    try {
      await removeLocalNote(noteId);
      if (selectedNote?.id === noteId) setSelectedNote(null);
      
      if (!String(noteId).startsWith('temp-')) {
        await api.deleteNote(noteId);
      }
      toast.info('Note deleted');
    } catch (err) {
      console.warn('[Notes] Note delete failed:', err);
      await addToQueue({ type: 'DELETE_NOTE', id: noteId });
      toast.info('Deleted locally (offline)');
      syncEngine.sync();
    }
  };

  const toggleFolder = (id) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleNoteSelect = (note) => {
    setSelectedNote(note);
    setSelectedFolderId(note.folder_id || (note.parent_id ? null : 'unfiled'));
    setMobileSidebarOpen(false);
  };

  const moveNoteToFolder = async (noteId, folderId) => {
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    
    if (note.folder_id === folderId) return;
    
    try {
      await saveLocalNote({ ...note, folder_id: folderId, pending: true });
      if (selectedNote?.id === noteId) {
        setSelectedFolderId(folderId || 'unfiled');
      }
      if (!String(noteId).startsWith('temp-')) {
        await api.updateNote(noteId, { folder_id: folderId });
      }
    } catch (err) {
      console.warn('[Notes] Note move failed:', err);
      await addToQueue({ type: 'UPDATE_NOTE', id: noteId, data: { folder_id: folderId } });
      syncEngine.sync();
    }
  };

  const onDropNote = (e, targetId, action) => {
    if (action === 'over') {
      setDragOverNote(targetId);
      return;
    }
    
    if (action === 'folder-over') {
      setDragOverFolder(targetId);
      return;
    }
    
    if (action === 'drop' || action === 'folder-drop') {
      e.preventDefault();
      setDragOverFolder(null);
      setDragOverNote(null);
      
      const noteId = e.dataTransfer.getData('text/plain');
      if (!noteId) return;
      
      if (action === 'folder-drop') {
        const targetFolderId = targetId === 'unfiled' ? null : targetId;
        moveNoteToFolder(noteId, targetFolderId);
      } else if (action === 'drop') {
        const targetNote = notes.find((n) => n.id === targetId);
        if (targetNote) {
          moveNoteToFolder(noteId, targetNote.folder_id);
        }
      }
    }
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
    setDragOverNote(null);
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    const noteId = e.dataTransfer.getData('text/plain');
    if (!noteId) return;
    
    moveNoteToFolder(noteId, folderId);
  };

  const handleNavigate = (id) => {
    const note = notes.find((n) => n.id === id);
    if (note) {
      handleNoteSelect(note);
    } else {
      const folder = folders.find((f) => f.id === id);
      if (folder) {
        setSelectedFolderId(id);
      }
    }
  };

  const isLoading = (foldersLoading || notesLoading) && notes.length === 0;

  return (
    <div className="relative flex h-[calc(100vh-8rem)] w-full overflow-hidden">
      {/* Mobile Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Left Panel - Sidebar */}
      <div
        className={cn(
          'flex flex-col border-r border-border/40 bg-panel/95 backdrop-blur-sm',
          'fixed md:relative inset-y-0 left-0 z-40 md:z-auto',
          'w-[85vw] max-w-[320px] md:w-[280px] md:max-w-none',
          'transition-transform duration-200 ease-out md:translate-x-0',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border/40">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-zinc-300">Notes</h2>
            {queue.length > 0 && (
              <span className="text-amber-500" title={`${queue.length} pending changes`}>
                <CloudOff size={12} />
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={loadData}
              className={cn(
                "p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition",
                apiLoading && "animate-spin"
              )}
              title="Sync with server"
              disabled={apiLoading}
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition"
              title="Search"
            >
              <Search size={14} />
            </button>
            <button
              onClick={createNote}
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition"
              title="New Note"
            >
              <Plus size={14} />
            </button>
            <button
              onClick={() => setMobileSidebarOpen(false)}
              className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition md:hidden"
              title="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {apiError && (
          <div className="bg-rose-500/10 border-b border-rose-500/20 p-2 text-[11px] text-rose-400 flex items-center gap-2">
            <CloudOff size={14} />
            <span>{apiError}</span>
          </div>
        )}

        {/* Search */}
        {showSearch && (
          <NotesSearch
            notes={notes}
            folders={folders}
            onNoteSelect={handleNoteSelect}
            onClose={() => setShowSearch(false)}
          />
        )}

        {/* Tree */}
        <div className="flex-1 overflow-y-auto py-2">
          {/* All Notes */}
          <button
            className={cn(
              'flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm transition',
              selectedFolderId === null && !selectedNote
                ? 'bg-accent/15 text-accent'
                : 'text-zinc-300 hover:bg-white/5',
              dragOverFolder === 'all' && 'bg-accent/20 ring-1 ring-accent',
            )}
            onClick={() => {
              setSelectedFolderId(null);
              setSelectedNote(null);
            }}
            onDragOver={(e) => handleDragOver(e, 'all')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, null)}
          >
            <FileText size={16} />
            <span>All Notes</span>
          </button>
          
          {/* Unfiled Notes Section */}
          {notes.filter(n => !n.folder_id && !n.parent_id).length > 0 && (
            <div key="unfiled-section">
              <div
                className={cn(
                  'group flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm transition cursor-pointer',
                  selectedFolderId === 'unfiled' ? 'bg-accent/15 text-accent' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-300',
                  dragOverFolder === 'unfiled' && 'bg-accent/20 ring-1 ring-accent'
                )}
                onClick={() => {
                  setSelectedFolderId('unfiled');
                  toggleFolder('unfiled');
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  onDropNote?.(e, 'unfiled', 'folder-over');
                }}
                onDragLeave={() => onDropNote?.(null, null, 'leave')}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverFolder(null);
                  const noteId = e.dataTransfer.getData('text/plain');
                  if (noteId) moveNoteToFolder(noteId, null);
                }}
              >
                <span
                  onClick={(e) => { e.stopPropagation(); toggleFolder('unfiled'); }}
                  className="cursor-pointer p-0.5 hover:bg-white/10 rounded"
                >
                  {expandedFolders.has('unfiled') ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <span>📄</span>
                <span className="truncate flex-1 font-medium">Unfiled Notes</span>
                <span className="text-[10px] bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded-full group-hover:bg-zinc-700 transition">
                  {notes.filter(n => !n.folder_id && !n.parent_id).length}
                </span>
              </div>
              
              {expandedFolders.has('unfiled') && (
                <div className="border-l border-zinc-800 ml-2.5 my-0.5">
                  <NoteTree
                    notes={notes}
                    initialNotes={notes.filter(n => !n.folder_id && !n.parent_id)}
                    expandedFolders={expandedFolders}
                    onToggle={toggleFolder}
                    depth={0}
                    onNoteSelect={handleNoteSelect}
                    selectedNoteId={selectedNote?.id}
                    onDropNote={onDropNote}
                    dragOverNote={dragOverNote}
                  />
                </div>
              )}
            </div>
          )}

          <div className="my-2 border-t border-border/40" />
          
          {/* Folder Tree */}
          {foldersLoading && apiLoading && folders.length === 0 && notes.length === 0 ? (
            <div className="px-4 py-2 text-xs text-zinc-500 italic">Loading folders...</div>
          ) : (
            <FolderTree
              folders={folders}
              notes={notes}
              selectedFolderId={selectedFolderId}
              onSelect={setSelectedFolderId}
              expandedFolders={expandedFolders}
              onToggle={toggleFolder}
              onDeleteFolder={deleteFolder}
              onNoteSelect={handleNoteSelect}
              selectedNoteId={selectedNote?.id}
              onDropNote={onDropNote}
              dragOverFolder={dragOverFolder}
            />
          )}

          {/* New Folder Input */}
          <div className="mt-2 px-2">
            {showNewFolder ? (
              <div className="flex items-center gap-1 rounded bg-zinc-900 p-1">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name..."
                  className="flex-1 bg-transparent px-2 py-1 text-xs outline-none"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                  onBlur={() => !newFolderName && setShowNewFolder(false)}
                />
                <button onClick={createFolder} className="p-1 hover:text-accent"><Plus size={14} /></button>
                <button onClick={() => setShowNewFolder(false)} className="p-1 hover:text-zinc-300"><X size={14} /></button>
              </div>
            ) : (
              <button
                onClick={() => setShowNewFolder(true)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wider text-zinc-500 transition hover:bg-white/5 hover:text-zinc-400"
              >
                <FolderPlus size={12} />
                <span>New Folder</span>
              </button>
            ) }
          </div>
        </div>
      </div>

      {/* Right Panel - Editor */}
      <div className="flex-1 flex flex-col min-w-0 bg-bg">
        <div className="flex-1 overflow-y-auto">
          {selectedNote ? (
            <NoteEditor
              note={selectedNote}
              notes={notes}
              folders={folders}
              onSave={saveNote}
              onDelete={deleteNote}
              onCreatePage={createChildPage}
              onNavigate={handleNavigate}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-zinc-500 px-4">
              <FileText size={48} className="mb-4 opacity-50" />
              <p className="text-lg font-medium text-center">Select a note to edit</p>
              <p className="text-sm text-center mt-1">Or create a new one from the sidebar</p>
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="mt-4 px-4 py-2 bg-accent text-white rounded-lg text-sm md:hidden"
              >
                Open Notes List
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
